import { log } from './vueLogger.js';

export class CacheManager {
	constructor(dbName, storeName, options = {}) {
		this.dbName = dbName;
		this.dbVersion = 1;
		this.storeName = storeName;
		this.keyPath = options.keyPath || 'key';
		this.db = null;
		this.maxAge = options.maxAge || 7 * 24 * 60 * 60 * 1000;
		this.memoryCache = new Map();
		this.memoryCacheTTL = options.memoryCacheTTL || 5 * 60 * 1000;
		this.logCategory = options.logCategory || 'Cache';
		this.keyPrefix = options.keyPrefix || null;
		this.initDB();
	}

	resolveKey(key) {
		if (!this.keyPrefix) return key;
		const prefix = typeof this.keyPrefix === 'function' ? this.keyPrefix() : this.keyPrefix;
		return `${prefix}${key}`;
	}

	async initDB() {
		return new Promise((resolve, reject) => {
			const request = indexedDB.open(this.dbName, this.dbVersion);

			request.onerror = () => reject(request.error);
			request.onsuccess = () => {
				this.db = request.result;
				resolve(this.db);
			};

			request.onupgradeneeded = (event) => {
				const db = event.target.result;
				if (!db.objectStoreNames.contains(this.storeName)) {
					const store = db.createObjectStore(this.storeName, { keyPath: this.keyPath });
					store.createIndex('timestamp', 'timestamp', { unique: false });
				}
			};
		});
	}

	async put(key, data) {
		if (!this.db) await this.initDB();

		return new Promise((resolve, reject) => {
			const transaction = this.db.transaction([this.storeName], 'readwrite');
			const store = transaction.objectStore(this.storeName);

			const cacheData = {
				[this.keyPath]: this.resolveKey(key),
				data: data,
				timestamp: Date.now(),
			};

			const request = store.put(cacheData);
			request.onsuccess = () => resolve();
			request.onerror = () => reject(request.error);
		});
	}

	async get(key) {
		if (!this.db) await this.initDB();

		return new Promise((resolve, reject) => {
			const transaction = this.db.transaction([this.storeName], 'readonly');
			const store = transaction.objectStore(this.storeName);

			const request = store.get(this.resolveKey(key));
			request.onsuccess = () => {
				const result = request.result;
				resolve(result ? result.data : null);
			};
			request.onerror = () => reject(request.error);
		});
	}

	async delete(key) {
		if (!this.db) await this.initDB();

		return new Promise((resolve, reject) => {
			const transaction = this.db.transaction([this.storeName], 'readwrite');
			const store = transaction.objectStore(this.storeName);

			const request = store.delete(this.resolveKey(key));
			request.onsuccess = () => resolve();
			request.onerror = () => reject(request.error);
		});
	}

	async clear() {
		if (!this.db) await this.initDB();

		return new Promise((resolve, reject) => {
			const transaction = this.db.transaction([this.storeName], 'readwrite');
			const store = transaction.objectStore(this.storeName);

			const request = store.clear();
			request.onsuccess = () => resolve();
			request.onerror = () => reject(request.error);
		});
	}

	async cleanOldEntries(maxAge = null) {
		if (!this.db) await this.initDB();

		const cutoff = Date.now() - (maxAge || this.maxAge);

		return new Promise((resolve, reject) => {
			const transaction = this.db.transaction([this.storeName], 'readwrite');
			const store = transaction.objectStore(this.storeName);
			const index = store.index('timestamp');

			const request = index.openCursor(IDBKeyRange.upperBound(cutoff));
			request.onsuccess = (event) => {
				const cursor = event.target.result;
				if (cursor) {
					cursor.delete();
					cursor.continue();
				} else {
					resolve();
				}
			};
			request.onerror = () => reject(request.error);
		});
	}

	getFromMemory(key) {
		const memCached = this.memoryCache.get(this.resolveKey(key));
		if (memCached && Date.now() - memCached.timestamp < this.memoryCacheTTL) {
			return memCached.data;
		}
		return null;
	}

	setInMemory(key, data) {
		this.memoryCache.set(this.resolveKey(key), {
			data: data,
			timestamp: Date.now(),
		});
	}

	deleteFromMemory(key) {
		this.memoryCache.delete(this.resolveKey(key));
	}

	clearMemory() {
		this.memoryCache.clear();
	}

	async getStats() {
		if (!this.db) await this.initDB();

		return new Promise((resolve, reject) => {
			const transaction = this.db.transaction([this.storeName], 'readonly');
			const store = transaction.objectStore(this.storeName);

			const countRequest = store.count();
			countRequest.onsuccess = () => {
				resolve({
					persistentEntries: countRequest.result,
					memoryEntries: this.memoryCache.size,
				});
			};
			countRequest.onerror = () => reject(countRequest.error);
		});
	}

	async clearAll() {
		log('info', this.logCategory, `🗱️ Clearing ${this.dbName} cache...`);

		try {
			await this.clear();
			this.clearMemory();
			log('success', this.logCategory, `✅ ${this.dbName} cache cleared successfully`);
		} catch (error) {
			log('error', this.logCategory, `❌ Failed to clear ${this.dbName} cache: ${error.message}`);
		}
	}

	async cleanOld(maxAgeInDays = null) {
		const maxAge = maxAgeInDays ? maxAgeInDays * 24 * 60 * 60 * 1000 : this.maxAge;
		try {
			await this.cleanOldEntries(maxAge);
		} catch (err) {
			log('error', this.logCategory, `Clean cache error: ${err.message}`);
		}
	}
}
